import { Injectable } from '@angular/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { ModalController } from '@ionic/angular';
import { ToastServiceService } from './toast-service.service';
import { HapticsService } from './haptics.service';
import { WalletPage } from '../pages/wallet/wallet.page';
import { DateTimeService } from './date-time.service';
import { DatabaseService } from './database.service';
import { StoryViewerComponent } from '../shared/components/story-viewer/story-viewer.component';
import { OutletPage } from '../pages/outlet/outlet.page';
import { UserChangeService } from './user-change.service';
import { User } from 'firebase/auth';
import { LandingPage } from '../pages/landing/landing.page';
import { KeyringService } from './keyring.service';
import { ShopPage } from '../pages/shop/shop.page';
import { LoadingService } from './loading.service';
import { AuthService } from './auth.service';
import { CloudfunctriggersService } from './cloudfunctriggers.service';
import { VouchersService } from './vouchers.service';
import { ConfettiService } from './confetti.service';
import { LoyaltyCardService } from './loyalty-card.service';
import { LoyaltyCardPreviewComponent } from '../shared/components/loyalty-card-preview/loyalty-card-preview.component';
import { OutletDealzService } from './outlet-dealz.service';
import { OneTimeStampQrCode } from '../models/interfaces';
import { AlertService } from './alert.service';
import { QrCodeClaimsService } from './qr-code-claims.service';
import { WalletService } from './wallet.service';

enum AcceptedParams {
  WALLET_TAB = 'wallet_tab',
  STORY = 'story',
  OUTLET = 'outlet',
  SHOP = 'shop',
  LOYALTY_PROGRAMME = 'loyalty_programme',
  UNCLAIMED_VOUCHER = 'unclaimed_voucher',
  KEYRING = 'keyring',
  UNCLAIMED_COUPON = 'unclaimed_coupon',
  ACTION ='action'
}

enum WalletTabParamValues {
  COUPONS = 'coupons',
  CARDS = 'cards',
  VOUCHERS = 'vouchers',
  REWARDS = 'rewards'
}

@Injectable({
  providedIn: 'root'
})
export class DeeplinkService {
 // private readonly domain: string = 'http://localhost:8100/';
  private listening = false;
  private activeModal: HTMLIonModalElement | null = null;
  private user:User
  constructor(
    private modalCtrl:ModalController,
    private toasts:ToastServiceService,
    private dateTimeService:DateTimeService,
    private dbService:DatabaseService,
    private loadingService:LoadingService,
    private userChangeService:UserChangeService,
    private qrCodeClaimsService:QrCodeClaimsService,
    private walletService:WalletService
  ) { 
    this.userChangeService.user$.subscribe(async (user) => {
      if(user){
        this.user = user
        this.initializeDeeplinks();
      }
    })
    
  }

  initializeDeeplinks(): void {
    if (this.listening) {
      console.log('Already listening for deep links');
      return;
    }
  
    App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
      console.log('Deep link received:', event.url);
    
      try {
          //await this.toasts.showToast(event.url,'Deep link received:')
          const url = new URL(event.url);
          const params: Record<string, string> = {};
          url.searchParams.forEach((value, key) => {
            params[key] = value;
          });

          // order is important. Descending priority.
          // urls with multiple params will do the most important only
          if(params[AcceptedParams.WALLET_TAB]){
              await this.walletService.checkWallet(params[AcceptedParams.WALLET_TAB] as WalletTabParamValues)
              return;
          }

          if(params[AcceptedParams.STORY]){
            await this.openStory(params[AcceptedParams.STORY]);
            return;
          }

          if(params[AcceptedParams.OUTLET]){
            await this.openOutletPage(params[AcceptedParams.OUTLET])
            return;
          }

          if(params[AcceptedParams.SHOP]){
            await this.openShopPage(params[AcceptedParams.SHOP])
            return;
          }

          if(params[AcceptedParams.ACTION]){
            const content = params[AcceptedParams.ACTION];
            const contentElements = content.split('_');
            const action = contentElements[0];
            const actionRelevantId  = contentElements[1];
            if(action =='gimmestamps') {
              await this.qrCodeClaimsService.claimSingleUseStampsQr(actionRelevantId)
            }

            if(action =='claimvoucher'){
              await this.qrCodeClaimsService.claimVoucher(actionRelevantId)
            }

            if(action =='claimcoupon'){
              await this.qrCodeClaimsService.claimCoupon(actionRelevantId)
            }

            if(action =='keyring'){
              await this.qrCodeClaimsService.claimKeyring(actionRelevantId)
            }

            if(action =='joinloyalty'){
              await this.joinLoyalty(actionRelevantId)
            }

            return;
          }

          if(params[AcceptedParams.KEYRING]){
            await  this.qrCodeClaimsService.claimKeyring(params[AcceptedParams.KEYRING])
            return;
          }

          console.log('Deep link params:', params);
         
      } catch (e) {
        await this.loadingService.hideLoading();
        console.error('Error parsing deep link URL:', e);
        await this.toasts.showToast(e.message?e.message:e?JSON.stringify(e):'Undefined error','Error parsing deep link URL:')
        await this.dbService.tryUploadScanError(
          e.message?e.message:e?JSON.stringify(e):'Undefined error',
          'none',
          'none',
          'SHOW',
          'QR'
        )   
      }
    });
  
    this.listening = true;
  }

  async openStory(storyId:string){
      this.loadingService.showOrContinueShowingLoading();
     // this.loadingService.updateLoaderMessage('fetching story...');
      const story = await this.dbService.fetchaStory(storyId)
      const shopId = story.restaurant_id
      const shop = await this.dbService.getShopWithId(shopId)
      if(this.activeModal){
        await this.activeModal.dismiss()
      }
      this.activeModal = await this.modalCtrl.create({
        component: StoryViewerComponent,
        initialBreakpoint: 1,
        breakpoints: [0,1],
        handle:false,
        mode:'ios',
        componentProps:{
          shop,
          stories:[story]
        }
      });
      await this.loadingService.hideLoading();
      await this.activeModal.present();
  }

  async joinLoyalty(loyaltyProgrammeId:string){
    this.loadingService.showOrContinueShowingLoading();
  //  this.loadingService.updateLoaderMessage('fetching loyalty programme...')
    await this.toasts.showToast('Deal page will open','Deep link params detected')
    await this.loadingService.hideLoading();
  }

  async openOutletPage(outletId:string){
    this.loadingService.showOrContinueShowingLoading()
   // this.loadingService.updateLoaderMessage('fetching shop...')
      const outlet = await this.dbService.getOutletFromCollectionGroup(outletId)
      if(this.activeModal){
        await this.activeModal.dismiss()
      }
      this.activeModal = await this.modalCtrl.create({
        component: OutletPage,
        mode: 'ios',
        initialBreakpoint: 1,
        breakpoints: [0, 1],
        handle:false,
        componentProps: {
          outlet:outlet,
          today:this.dateTimeService.giveLocalNowDay()
        }
      });
      await this.loadingService.hideLoading();
      await this.activeModal.present();
 
  }

  async openShopPage(shopId:string){
      this.loadingService.showOrContinueShowingLoading()
     // this.loadingService.updateLoaderMessage('fetching shop...')
      const shop = await this.dbService.getShopWithId(shopId)
      const outlets = await this.dbService.getShopOutlets(shopId)
      const modal = await this.modalCtrl.create({
        component: ShopPage,
        mode: 'ios',
        initialBreakpoint: 1,
        breakpoints: [0, 1],
        handle:false,
        componentProps: {
          shop,
          shopOutlets:outlets
        }
      });
      await this.loadingService.hideLoading()
      await modal.present();
  }

  
}

